import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { DirectionsBoat, Star, Security, Speed, AdminPanelSettings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const HeroSection = () => {
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();

  // Brand colors: Orange and Navy Blue
  const brandOrange = '#FF6B35';
  const brandNavy = '#1a237e';

  // Water, Lake, Sailing & Summer themed animations
  const waterKeyframes = `
    @keyframes gradient-shift {
      0%, 100% {
        background-position: 50% 0%;
      }
      50% {
        background-position: 50% 100%;
      }
    }
    @keyframes water-ripple {
      0% {
        transform: scale(0.8);
        opacity: 0.6;
      }
      50% {
        transform: scale(1.2);
        opacity: 0.3;
      }
      100% {
        transform: scale(1.6);
        opacity: 0;
      }
    }
    @keyframes bubble-rise {
      0% {
        transform: translateY(100vh) translateX(0);
        opacity: 0;
      }
      10% {
        opacity: 0.6;
      }
      90% {
        opacity: 0.6;
      }
      100% {
        transform: translateY(-100px) translateX(20px);
        opacity: 0;
      }
    }
    @keyframes bubble-rise-2 {
      0% {
        transform: translateY(100vh) translateX(0);
        opacity: 0;
      }
      10% {
        opacity: 0.4;
      }
      90% {
        opacity: 0.4;
      }
      100% {
        transform: translateY(-100px) translateX(-30px);
        opacity: 0;
      }
    }
    @keyframes sail-float {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-6px);
      }
    }
    @keyframes sun-rays {
      0%, 100% {
        opacity: 0.2;
      }
      50% {
        opacity: 0.32;
      }
    }
    @keyframes water-reflection {
      0% {
        transform: translateX(-100px) skewX(-5deg);
        opacity: 0.2;
      }
      50% {
        transform: translateX(0) skewX(0deg);
        opacity: 0.4;
      }
      100% {
        transform: translateX(100px) skewX(5deg);
        opacity: 0.2;
      }
    }
    @keyframes wave-motion {
      0%, 100% {
        transform: translateX(0) translateY(0);
      }
      50% {
        transform: translateX(-2%) translateY(1px);
      }
    }
    @keyframes slide-in-left {
      from {
        opacity: 0;
        transform: translateX(-50px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    @keyframes slide-in-right {
      from {
        opacity: 0;
        transform: translateX(50px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    @keyframes fade-in-up {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes shimmer-water {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }
  `;

  const features = [
    {
      icon: <DirectionsBoat sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />,
      title: 'Profesjonalne Jachty',
      description: 'Najwyższej jakości jednostki pływające'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />,
      title: 'Bezpieczeństwo',
      description: 'Wszystkie jachty są w pełni wyposażone'
    },
    {
      icon: <Star sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />,
      title: 'Doświadczenie',
      description: 'Wieloletnie doświadczenie w czarterze'
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />,
      title: 'Rezerwacja',
      description: 'Kontakt telefoniczny lub mailowy'
    }
  ];

  return (
    <>
      <style>{waterKeyframes}</style>
      <Box
        sx={{
          minHeight: { xs: '60vh', sm: '70vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(180deg, #87CEEB 0%, #4682B4 30%, #1e3a5f 60%, ${brandNavy} 100%)`,
          backgroundSize: '100% 220%',
          animation: 'gradient-shift 28s ease-in-out infinite'
        }}
      >
      {/* Base Lake/Water Gradient Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(180deg, #87CEEB 0%, #5F9EA0 25%, #4682B4 50%, #2d5a87 75%, ${brandNavy} 100%)`,
          backgroundSize: '100% 220%',
          animation: 'gradient-shift 28s ease-in-out infinite',
          zIndex: 0
        }}
      />

      {/* Sun Rays - Summer Light Effect */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '10%',
          width: { xs: 300, md: 500 },
          height: { xs: 300, md: 500 },
          background: 'radial-gradient(circle, rgba(255, 215, 0, 0.3), transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(30px)',
          animation: 'sun-rays 14s ease-in-out infinite',
          zIndex: 1
        }}
      />

      {/* Water Ripples */}
      {[...Array(2)].map((_, i) => (
        <Box
          key={`ripple-${i}`}
          sx={{
            position: 'absolute',
            bottom: `${18 + i * 10}%`,
            left: `${28 + i * 26}%`,
            width: { xs: 100 + i * 20, md: 140 + i * 30 },
            height: { xs: 100 + i * 20, md: 140 + i * 30 },
            borderRadius: '50%',
            border: `1px solid rgba(255, 255, 255, ${0.14 - i * 0.03})`,
            animation: `water-ripple ${7 + i * 1.5}s ease-out infinite`,
            animationDelay: `${i * 1.1}s`,
            zIndex: 1
          }}
        />
      ))}

      {/* Rising Bubbles from Water */}
      {[...Array(0)].map((_, i) => (
        <Box
          key={`bubble-${i}`}
          sx={{
            position: 'absolute',
            bottom: '-20px',
            left: `${5 + i * 8}%`,
            width: { xs: 8 + (i % 3) * 4, md: 12 + (i % 3) * 6 },
            height: { xs: 8 + (i % 3) * 4, md: 12 + (i % 3) * 6 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.2))',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            animation: i % 2 === 0 ? `bubble-rise ${8 + (i % 4) * 2}s linear infinite` : `bubble-rise-2 ${10 + (i % 4) * 2}s linear infinite`,
            animationDelay: `${i * 0.5}s`,
            zIndex: 1
          }}
        />
      ))}

      {/* Sailing Boat Silhouette */}
      <Box
        sx={{
          position: 'absolute',
          right: { xs: '-80px', md: '5%' },
          bottom: { xs: '15%', md: '20%' },
          zIndex: 2,
          opacity: 0.25,
          transform: { xs: 'scale(0.5)', md: 'scale(1)' },
        }}
      >
        <DirectionsBoat 
          sx={{ 
            fontSize: { xs: 250, md: 450 },
            color: 'rgba(255, 255, 255, 0.4)',
            filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
            animation: 'sail-float 6s ease-in-out infinite',
          }} 
        />
      </Box>

      {/* Smooth lower blend (without geometric bars) */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '36%',
          background: 'linear-gradient(to top, rgba(26, 35, 126, 0.52), rgba(61, 126, 178, 0.2), transparent)',
          zIndex: 1
        }}
      />

      {/* Water Reflection - Light on Water */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '-10%',
          right: '-10%',
          height: '15%',
          background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)',
          transform: 'skewX(-5deg)',
          animation: 'water-reflection 12s ease-in-out infinite',
          zIndex: 1
        }}
      />

      {/* Shimmer Water Effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer-water 18s linear infinite',
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />

      {/* Gradient Overlay for Text Readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to right, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />
      
      {/* Subtle Vignette */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.15) 100%)',
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left Column - Main Content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "'Inter', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontWeight: 700,
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem', lg: '3.5rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  animation: 'slide-in-left 1s ease-out',
                  opacity: 0,
                  animationFillMode: 'forwards'
                }}
              >
                Profesjonalny
                <br />
                <Box component="span" sx={{ color: brandOrange }}>
                  Czarter Jachtów
                </Box>
              </Typography>
              
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "'Inter', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  color: 'white',
                  mb: 3,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  fontWeight: 300,
                  lineHeight: 1.6,
                  textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                  animation: 'fade-in-up 1s ease-out 0.3s',
                  opacity: 0,
                  animationFillMode: 'forwards'
                }}
              >
                Odkryj piękno Mazur na pokładzie naszych jachtów. 
                Bezpieczeństwo, komfort i niezapomniane wspomnienia gwarantowane.
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1.5, sm: 2 }, 
                justifyContent: { xs: 'center', md: 'flex-start' }, 
                flexWrap: 'wrap',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                animation: 'fade-in-up 1s ease-out 0.6s',
                opacity: 0,
                animationFillMode: 'forwards'
              }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    const yachtsSection = document.getElementById('yachts-section');
                    if (yachtsSection) {
                      yachtsSection.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }}
                  sx={{
                    backgroundColor: brandOrange,
                    color: 'white',
                    fontWeight: 600,
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '280px', sm: 'none' },
                    '&:hover': {
                      backgroundColor: '#FF5252',
                      transform: 'translateY(-2px) scale(1.05)',
                      boxShadow: `0 6px 20px ${brandOrange}40`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Zobacz Nasze Jachty
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/contact')}
                  sx={{
                    borderColor: 'white',
                    borderWidth: 1.5,
                    color: 'white',
                    fontWeight: 600,
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    borderRadius: '8px',
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '280px', sm: 'none' },
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      borderColor: 'white',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(255,255,255,0.2)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Kontakt
                </Button>

                {/* Admin Panel Button */}
                {currentUser && isAdmin && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/admin')}
                    startIcon={<AdminPanelSettings />}
                    sx={{
                      borderColor: brandNavy,
                      color: brandNavy,
                      borderWidth: 1.5,
                      fontWeight: 600,
                      px: { xs: 3, sm: 4 },
                      py: 1.5,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      borderRadius: '8px',
                      width: { xs: '100%', sm: 'auto' },
                      maxWidth: { xs: '280px', sm: 'none' },
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      '&:hover': {
                        backgroundColor: brandNavy,
                        color: 'white',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(30, 58, 95, 0.3)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    Panel Admina
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right Column - Features Cards */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {features.map((feature, index) => (
                <Grid item xs={6} sm={6} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      animation: `slide-in-right 0.8s ease-out ${0.4 + index * 0.1}s`,
                      opacity: 0,
                      animationFillMode: 'forwards',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        borderColor: brandOrange
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Box sx={{ mb: 1 }}>
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "'Inter', 'Montserrat', sans-serif",
                          fontWeight: 600,
                          color: 'white',
                          fontSize: '0.9rem',
                          mb: 0.5,
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "'Inter', 'Montserrat', sans-serif",
                          color: 'rgba(255,255,255,0.9)',
                          fontSize: '0.8rem',
                          lineHeight: 1.4,
                          textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
    </>
  );
};

export default HeroSection;
